import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import ButtonLink from "./ButtonLink";

import ConversationInterface from "./ConversationInterface";

import { Button, Loader } from "@mantine/core";

import ConversationIcon, {
	userSourceName,
	aiSourceName,
	formatTimestamp,
} from "./ChatInterface/ConversationIcon";

import {
	aiConversationAboutDocument,
	suggestQuestions,
	summarizeDocument,
} from "../actions/documentActions";
import { IconAlertTriangleFilled } from "@tabler/icons-react";
import { getAiOpeningGreeting } from "../utilities/utilities";

//if the remaining tokens are less than this value, the AI will only consider the last two messages
const remainingTokensThreshold = 900;

const PreCannedQuestionButtons = ({ prompts, headingText }) => {
	return prompts.length ? (
		<div>
			<span style={{ fontFamily: "MainFont-Bold", margin: 8 }}>
				{headingText}
			</span>
			<div style={{ display: "grid", justifyItems: "start" }}>
				{prompts.map(({ text, handler }, index) => {
					return (
						<div key={"q" + index} style={{ margin: 12 }}>
							<Button
								variant="default"
								onClick={() => handler(text)}
								styles={{
									root: {
										height: "auto",
										paddingBlock:
											"calc((var(--button-height) - 1em) / 2)",
										maxWidth: "100%",
									},
									inner: {
										height: "auto",
										justifyContent: "flex-start",
									},
									label: {
										whiteSpace: "normal",
										height: "auto",
										overflow: "visible",
										textAlign: "left",
									},
								}}
							>
								{text}
							</Button>
						</div>
					);
				})}
			</div>
		</div>
	) : null;
};

const DocumentAIPane = ({
	documentID,
	setHeaderExtraInfo,
	messagesChanged,
	openDocument,
	downloadCopyOfDocument,
	docCanBeOpenedInLauncher,
}) => {
	const [suggestedQuestions, setSuggestedQuestions] = useState([]);
	const [messages, setMessages] = useState([]);
	const [loading, setLoading] = useState(true);
	const [firstQuestion, setFirstQuestion] = useState(true);
	const [responseCode, setResponseCode] = useState(null);
	const [messageContextStartIndex, setMessageContextStartIndex] = useState(0);
	const [lastMessageInError, setLastMessageInError] = useState(false);
	const [openingGreeting, setOpeningGreeting] = useState(null);
	const userDisplayName = useSelector((state) => state.user.userDisplayName);

	useEffect(() => {
		messagesChanged(messages);
	}, [messages]);

	//initally request suggested questions about the doc
	useEffect(() => {
		//if the user navigates away before response arrives, the component
		//is unmounted and attempting to set state results in an error
		let componentIsUnmounted = false;

		setHeaderExtraInfo({ display: "", tooltip: "" });
		suggestQuestions(documentID)
			.then(({ code, followup, meta }) => {
				if (!componentIsUnmounted) {
					setLoading(false);
					setResponseCode(code);
					if (code === "OKAY") {
						checkTruncation(meta);
						setSuggestedQuestions(followup);
					}
				}
			})
			.catch(() => {
				setLoading(false);
				setLastMessageInError(true);
			});
		return () => {
			componentIsUnmounted = true;
		};
	}, []);

	const checkTruncation = ({ proportionUsed, pctUsed, usage }) => {
		if (proportionUsed < 1) {
			setHeaderExtraInfo({
				display: `(${pctUsed} Considered)`,
				tooltip: `      There is a limit to how much of a document LexWorkplace AI can keep in its "context window".  If the document is longer than this limit, it will use as much of the document as possible.  In this case, LexWorkplace AI's responses are only considering the first ${pctUsed} of the document.`,
			});
		} else {
			setHeaderExtraInfo({ display: "", tooltip: "" });
		}
		if (usage) {
			if (usage.remainingTokens) {
				if (usage.remainingTokens < remainingTokensThreshold) {
					console.log(
						"AI token context threshhold exceeded, resetting the message context"
					);
					setMessageContextStartIndex(messages.length - 2);
				}
			}
		}
	};

	const appendMessage = (source, content) => {
		setMessages((oldArray) => [
			...oldArray,
			{
				source,
				content,
				animateTyping: source === aiSourceName,
				timestamp: formatTimestamp(new Date()),
			},
		]);
	};

	const priorMessages = messages.slice(messageContextStartIndex).map((m) => {
		return {
			role: m.source === userSourceName ? "user" : "assistant",
			content: m.content,
		};
	});

	const processUserPromptAndAIResponse = (userPrompt, fn) => {
		appendMessage(userSourceName, userPrompt);
		if (firstQuestion) {
			setOpeningGreeting(getAiOpeningGreeting(userDisplayName));
		}
		setLoading(true);
		fn()
			.then(({ reply, followup, meta }) => {
				setLastMessageInError(false);
				checkTruncation(meta);
				setFirstQuestion(false);
				setLoading(false);
				if (followup) {
					setSuggestedQuestions(followup);
				}
				appendMessage(aiSourceName, reply);
			})
			.catch(() => {
				setFirstQuestion(false);
				setLoading(false);
				setLastMessageInError(true);
			});
	};

	const preCannedPrompts = (
		firstQuestion
			? [
					{
						text: "Summarize this Document",
						handler: () =>
							processUserPromptAndAIResponse(
								"Please provide a brief summary of this document",
								() => summarizeDocument(documentID)
							),
					},
				]
			: []
	).concat(
		suggestedQuestions.map((suggestedPrompt) => {
			return {
				text: suggestedPrompt,
				handler: (userPrompt) => {
					processUserPromptAndAIResponse(userPrompt, () =>
						aiConversationAboutDocument({
							documentID,
							userPrompt,
							priorMessages,
						})
					);
				},
			};
		})
	);

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				flexGrow: 1,
				overflow: "hidden",
				padding: "16px 16px 16px 40px",
			}}
		>
			<ConversationInterface
				messages={messages}
				submitQuestion={(userPrompt) => {
					processUserPromptAndAIResponse(userPrompt, () =>
						aiConversationAboutDocument({
							documentID,
							userPrompt,
							priorMessages,
						})
					);
				}}
				IconComponent={ConversationIcon}
				userTextInputAllowed={responseCode !== "NO_CONTENT"}
				endOfMessagesComponent={
					loading ? (
						<div style={{ padding: 24 }}>
							<center>
								{firstQuestion && openingGreeting ? (
									<p>{openingGreeting}</p>
								) : null}
								<Loader size={28} />
							</center>
						</div>
					) : lastMessageInError ? (
						<div style={{ padding: 24 }}>
							<div
								style={{
									width: "100%",
									textAlign: "center",
								}}
							>
								<p>
									<IconAlertTriangleFilled
										size={24}
										color="var(--lw-darker-red)"
										style={{ verticalAlign: "middle" }}
									/>
									<span
										style={{ paddingLeft: 4 }}
									>{`LexWorkplace AI encountered an error`}</span>
								</p>
								{docCanBeOpenedInLauncher ? (
									<ButtonLink onClick={openDocument}>
										Open Document
									</ButtonLink>
								) : (
									<ButtonLink
										onClick={downloadCopyOfDocument}
									>
										Download a Copy of Document
									</ButtonLink>
								)}
							</div>
						</div>
					) : responseCode === "NO_CONTENT" ? (
						<div style={{ padding: 24 }}>
							<div
								style={{
									width: "100%",
									textAlign: "center",
									paddingTop: "40%",
								}}
							>
								<p>{`The content of this document is not available for AI analysis.`}</p>
								{docCanBeOpenedInLauncher ? (
									<ButtonLink onClick={openDocument}>
										Open Document
									</ButtonLink>
								) : (
									<ButtonLink
										onClick={downloadCopyOfDocument}
									>
										Download a Copy of Document
									</ButtonLink>
								)}
							</div>
						</div>
					) : (
						<PreCannedQuestionButtons
							headingText={
								firstQuestion
									? "LexWorkplace AI Suggested Prompts"
									: "Suggested Followup Questions"
							}
							prompts={preCannedPrompts}
						/>
					)
				}
			/>
		</div>
	);
};

export default DocumentAIPane;
